import { FC, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Calendar } from 'primereact/calendar';
import { OverlayPanel } from 'primereact/overlaypanel';
import { FormEvent } from 'primereact/ts-helpers';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import humanizeDate from '../../utils/humanize-date';
import { PeriodoModo, PeriodoSelectorProps } from './types';
import PERIODO_OPTIONS from './utils';

const PeriodoSelector: FC<PeriodoSelectorProps> = ({
  dateRange,
  onDateRangeChange,
  onOverlayVisibleChange,
}) => {
  const [periodoModo, setPeriodoModo] = useState<PeriodoModo>(null);
  const [periodoCustomDraft, setPeriodoCustomDraft] = useState<Date[]>([]);
  const periodoOverlayReferencia = useRef<OverlayPanel>(null);

  const periodoLabel =
    dateRange && dateRange.length >= 2 && dateRange[1] != null
      ? `${humanizeDate(dateRange[0])} - ${humanizeDate(dateRange[1])}`
      : PERIODO_OPTIONS.find((opcao) => opcao.value === periodoModo)?.label ??
        'Selecione o período';

  const isAplicarDesabilitado =
    periodoModo !== 'customizado' ||
    periodoCustomDraft.length < 2 ||
    periodoCustomDraft[1] == null;

  const handlePeriodoModoClick = useImmutableCallback(
    (evento: React.MouseEvent<HTMLButtonElement>) => {
      const modo = evento.currentTarget.value as Exclude<PeriodoModo, null>;
      setPeriodoModo(modo);
      if (modo === '7dias') {
        onDateRangeChange([
          dayjs().subtract(7, 'day').toDate(),
          dayjs().toDate(),
        ]);
      } else if (modo === '30dias') {
        onDateRangeChange([
          dayjs().subtract(30, 'day').toDate(),
          dayjs().toDate(),
        ]);
      } else {
        onDateRangeChange(null);
      }
    },
  );

  const handlePeriodoCustomChange = useImmutableCallback(
    (event: FormEvent<(Date | null)[]>) => {
      setPeriodoCustomDraft((event.value as Date[]) ?? []);
    },
  );

  const handleLimpar = useImmutableCallback(() => {
    setPeriodoModo(null);
    setPeriodoCustomDraft([]);
    onDateRangeChange(null);
    periodoOverlayReferencia.current?.hide();
    onOverlayVisibleChange?.(false);
  });

  const handleAplicar = useImmutableCallback(() => {
    onDateRangeChange(periodoCustomDraft);
    periodoOverlayReferencia.current?.hide();
    onOverlayVisibleChange?.(false);
  });

  return (
    <>
      <label>Período</label>
      <button
        type="button"
        className="p-dropdown p-component p-inputwrapper w-full dropdown-modal-periodo-botao"
        onClick={(evento) => periodoOverlayReferencia.current?.toggle(evento)}
      >
        <span className="p-dropdown-label p-inputtext">{periodoLabel}</span>
        <div className="p-dropdown-trigger">
          <span className="p-dropdown-trigger-icon pi pi-chevron-down" />
        </div>
      </button>
      <OverlayPanel
        ref={periodoOverlayReferencia}
        className="dropdown-modal-periodo-overlay"
        dismissable
        onShow={() => onOverlayVisibleChange?.(true)}
        onHide={() => onOverlayVisibleChange?.(false)}
      >
        <div className="periodo-opcoes-lista">
          {PERIODO_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              value={option.value}
              // prettier-ignore
              className={`periodo-opcao-btn${
                periodoModo === option.value
                  ? ' periodo-opcao-btn--active'
                  : ''
              }`}
              onClick={(evento) => {
                handlePeriodoModoClick(evento);
                if (option.value !== 'customizado') {
                  periodoOverlayReferencia.current?.hide();
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        {periodoModo === 'customizado' && (
          <>
            <p className="periodo-overlay-label">Selecione o intervalo</p>
            <Calendar
              value={
                periodoCustomDraft.length > 0 ? periodoCustomDraft : undefined
              }
              onChange={handlePeriodoCustomChange}
              selectionMode="range"
              inline
              dateFormat="dd/mm/yy"
              showButtonBar={false}
            />
          </>
        )}
        <div className="periodo-overlay-actions">
          <button
            type="button"
            className="p-button p-component p-button-text p-button-sm"
            onClick={handleLimpar}
          >
            <span className="p-button-label p-c">Limpar</span>
          </button>
          <button
            type="button"
            className="p-button p-component p-button-sm"
            onClick={handleAplicar}
            disabled={isAplicarDesabilitado}
          >
            <span className="p-button-label p-c">Aplicar</span>
          </button>
        </div>
      </OverlayPanel>
    </>
  );
};

export default PeriodoSelector;
